import React, { useEffect, useState } from "react";
import { Box, Container, IconButton, Stack, Typography, Card } from "@mui/joy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
//import { ChangePasswordForm } from "../components/auth/ChangePasswordForm";
import { ProfileHeader } from "../components/Profile/ProfileHeader";
import { ProfileStats } from "../components/Profile/ProfileStats";
import { TravelStamps } from "../components/Profile/TravelStamps";
import { TravelBadges } from "../components/Profile/TravelBadges";
import { ProfileLists } from "../components/Profile/ProfileLists";
import { ProfileListDetail } from "../components/Profile/ProfileListDetail";
import { FriendsList } from "../components/Profile/FriendsList";
import { useProfileStore } from "../store/profileStore";
import { useListStore } from "../store/listStore";
import { usePlaceStore } from "../store/placeStore";
import { useFollowStore } from "../store/followStore";
import { useAuthStore } from "../store/authStore";
import { Database } from "../lib/database";
import { supabase } from "../lib/supabase";

type Place = Database["public"]["Tables"]["places"]["Row"];

interface TargetProfile {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  privacy_settings: JSON | null;
}

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId?: string }>();
  const { profile } = useAuthStore();
  const {
    stats,
    badges,
    manualCountries,
    loading: profileLoading,
    fetchProfileStats,
    addManualCountry,
    removeManualCountry,
  } = useProfileStore();
  const {
    listsWithPlaceCounts,
    listsLoading: listLoading,
    fetchListsWithPlaceCounts,
  } = useListStore();
  const { fetchPlacesForList } = usePlaceStore();
  const { fetchFollowing } = useFollowStore();

  const [view, setView] = useState<"profile" | "friends">("profile");
  const [expandedListId, setExpandedListId] = useState<string | null>(null);
  const [expandedListPlaces, setExpandedListPlaces] = useState<Place[]>([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);
  const [targetProfile, setTargetProfile] = useState<TargetProfile | null>(
    null,
  );
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Reset view state when navigating to a different profile
  useEffect(() => {
    setView("profile");
    setExpandedListId(null);
    setExpandedListPlaces([]);
  }, [userId]);

  // Determine if viewing own profile
  const isOwnProfile = !userId || userId === profile?.id;
  const displayUserId = userId || profile?.id;

  // Clear target profile if viewing own profile (during render)
  if ((!userId || userId === profile?.id) && targetProfile !== null) {
    setTargetProfile(null);
  }

  // Fetch target user profile if viewing another user
  useEffect(() => {
    // Skip if viewing own profile
    if (!userId || userId === profile?.id) {
      return;
    }

    const fetchTargetProfile = async () => {
      setFetchingProfile(true);
      setProfileError(null);
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, username, full_name, avatar_url, bio, privacy_settings")
          .eq("id", userId)
          .maybeSingle();

        if (error) {
          console.error("Error fetching target profile:", error);
          setProfileError("Failed to load profile");
          return;
        }

        if (!data) {
          // Profile exists but is blocked by RLS (likely private)
          // or profile doesn't exist at all
          setProfileError("This profile is private");
          return;
        }

        // Double-check privacy settings (shouldn't be needed with RLS but good practice)
        const privacySettings = data.privacy_settings;
        if (privacySettings?.profile_public === false) {
          setProfileError("This profile is private");
          return;
        }

        setTargetProfile(data);
      } catch (error) {
        console.error("Error fetching target profile:", error);
        setProfileError("Failed to load profile");
      } finally {
        setFetchingProfile(false);
      }
    };

    fetchTargetProfile();
  }, [userId, profile?.id]);

  // Fetch profile data on mount
  useEffect(() => {
    if (displayUserId) {
      fetchProfileStats(displayUserId);
      fetchListsWithPlaceCounts(displayUserId);
    }
  }, [displayUserId, fetchProfileStats, fetchListsWithPlaceCounts]);

  // Fetch following list for current user
  useEffect(() => {
    if (profile?.id) {
      fetchFollowing(profile.id);
    }
  }, [profile?.id, fetchFollowing]);

  const handleListClick = async (listId: string) => {
    if (expandedListId === listId) {
      // Collapse if clicking the same list
      setExpandedListId(null);
      setExpandedListPlaces([]);
    } else {
      // Expand and fetch places
      setExpandedListId(listId);
      setLoadingPlaces(true);
      try {
        const places = await fetchPlacesForList(listId);
        setExpandedListPlaces(places);
      } finally {
        setLoadingPlaces(false);
      }
    }
  };

  const handleCloseDetail = () => {
    setExpandedListId(null);
    setExpandedListPlaces([]);
  };

  const handleViewOnMap = () => {
    if (!expandedListId) return;
    const mode = isOwnProfile ? "edit" : "view";
    navigate(`/?listId=${expandedListId}&mode=${mode}`);
  };

  const expandedList = listsWithPlaceCounts.find(
    (l) => l.id === expandedListId,
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "background.surface",
        paddingBottom: 4,
      }}
    >
      <Container maxWidth="md" sx={{ paddingTop: 2 }}>
        {/* Back Button */}
        <IconButton
          variant="outlined"
          size="sm"
          onClick={() => navigate(-1)}
          aria-label="back"
          sx={{ marginBottom: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>

        {/* Show error or loading state for target profile */}
        {!isOwnProfile && (fetchingProfile || profileError) ? (
          <Card variant="outlined" sx={{ padding: 4, textAlign: "center" }}>
            <Typography level="body-md" sx={{ color: "text.secondary" }}>
              {fetchingProfile ? "Loading profile..." : profileError}
            </Typography>
          </Card>
        ) : view === "friends" ? (
          <FriendsList onBack={() => setView("profile")} />
        ) : (
          <Stack spacing={4}>
            {/* Profile Header */}
            <ProfileHeader
              targetProfile={targetProfile || undefined}
              isOwnProfile={isOwnProfile}
            />

            {/* Profile Stats */}
            <ProfileStats
              stats={stats}
              loading={profileLoading}
              onFriendsClick={() => setView("friends")}
            />

            {/* Travel Stamps */}
            <TravelStamps
              countries={stats?.countries || []}
              isOwnProfile={isOwnProfile}
              manualCountries={manualCountries}
              onAddCountry={addManualCountry}
              onRemoveCountry={removeManualCountry}
            />

            {/* Travel Badges */}
            <TravelBadges badges={badges} />

            {/* Lists Section */}
            <Box>
              <ProfileLists
                lists={listsWithPlaceCounts}
                loading={listLoading}
                onListClick={handleListClick}
                expandedListId={expandedListId}
                isOwnProfile={isOwnProfile}
              />

              {/* Expanded List Detail */}
              {expandedListId && expandedList && (
                <Box marginTop={2}>
                  <ProfileListDetail
                    list={expandedList}
                    places={expandedListPlaces}
                    loading={loadingPlaces}
                    onClose={handleCloseDetail}
                    onViewOnMap={handleViewOnMap}
                  />
                </Box>
              )}
            </Box>

            {/* Change Password - own profile only */}
            {/* {isOwnProfile && <ChangePasswordForm />} */}
          </Stack>
        )}
      </Container>
    </Box>
  );
};
